import Service from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { isNone } from '@ember/utils';
import { next } from '@ember/runloop';
import { buildWaiter } from '@ember/test-waiters';

let waiter = buildWaiter('highcharts-service:import-waiter');

export default class HighchartsService extends Service {
  @tracked
  Highcharts: typeof import('highcharts') | null = null;

  loadHighcharts = task(async () => {
    let token = waiter.beginAsync();

    let highcharts = await import('highcharts');
    let Highcharts = highcharts.default;

    // Import and add modules to Highcharts namespace
    let annotations = await import('highcharts/modules/annotations');
    annotations.default(Highcharts);
    let exporting = await import('highcharts/modules/exporting');
    exporting.default(Highcharts);
    let offlineExporting = await import('highcharts/modules/offline-exporting'); // default uses HighCharts server for export due to legacy browser support
    offlineExporting.default(Highcharts);
    let exportData = await import('highcharts/modules/export-data'); // allows exporting to formats like CSV
    exportData.default(Highcharts);

    // Override default line legend symbol drawer with the column one - we want
    // to draw rounded squares for all legend symbols.
    (Highcharts as any).seriesTypes.line.prototype.drawLegendSymbol = (
      Highcharts as any
    ).seriesTypes.column.prototype.drawLegendSymbol;

    Highcharts.setOptions({
      lang: {
        thousandsSep: ',',
      },
    });

    // https://www.highcharts.com/docs/chart-concepts/labels-and-string-formatting#filtering
    // Allow SVG attributes so that custom highcharts tooltips render correctly
    // eslint-disable-next-line spellcheck/spell-checker
    Highcharts.AST.allowedAttributes.push('viewBox', 'xmlns');

    this.Highcharts = Highcharts;

    // We want a way to be able to access the Highcharts library from
    // Highcharts settings (like the tooltip point formatter). Highcharts
    // doesn't reference the base library inside those options, and we
    // load it asynchronously, so it's not easily available (we can't import
    // Highcharts in the utilities/graphs.ts file because of the async nature).
    // So we set it globally on window, so that we can access it from weird
    // places, like custom Highcharts config options.
    window.Highcharts = Highcharts;

    waiter.endAsync(token);
  });

  willDestroy() {
    super.willDestroy();

    if (this.Highcharts?.charts) {
      this.Highcharts.charts.forEach((chart) => {
        // Throw this onto the event loop because the HCBaseChart also destroys the chart when the
        // component will be destroyed, potentially causing a race condition when this service tries
        // to destroy it again. After a tick, the heuristics (the isNone calls) match properly.
        next(this, () => {
          if (!isNone(chart) && !isNone(chart.renderer)) {
            chart.destroy();
          }
        });
      });
    }

    this.Highcharts = null;
    // Cast to override "The operand of a 'delete' operator must be optional"
    delete (window as any).Highcharts;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    highcharts: HighchartsService;
  }
}
